import { cloneDeep } from 'lodash';
import moment from 'moment';

import { slugifyName } from 'bundles/common/utils/commonUtils';
import type {
  CourseMaterialsData,
  CourseMaterialsDataModule,
  CourseMaterialsDataModuleLesson,
  CourseMaterialsDataModuleLessonItem,
} from 'bundles/in-course-stores/constants/CourseType';
import type { GetInjectableActivities_ElementsFragment as InjectableActivity } from 'bundles/ondemand-common/activity-injection/queries/__generated__/GetInjectableActivities';

/**
 * Converts an injectable activity to a raw item object, ready to be inserted
 * into the `courseMaterialsData` object in the CourseStore.
 *

 * @param injectableActivity - The injectable activity to convert to a raw item.
 * @returns A raw item object compatible with items in the `courseMaterialsData`.
 */
export const activityToRawItem = (injectableActivity: InjectableActivity): CourseMaterialsDataModuleLessonItem => {
  return {
    contentSummary: {
      typeName: 'injectable',
      definition: {
        injectableActivityId: injectableActivity.id,
        injectableTypeName: injectableActivity.type.typeName,
        placement: {
          adjacentItemId: injectableActivity.placement.adjacentItemId,
          relativePosition: injectableActivity.placement.relativePosition.toLowerCase() as 'before' | 'after',
        },
      },
    },
    id: injectableActivity.placement.itemId,
    name: injectableActivity.placement.displayName,
    slug: slugifyName(injectableActivity.placement.displayName),
    timeCommitment: moment.duration(injectableActivity.placement.timeCommitment).asMilliseconds(),
    trackId: 'none',
  };
};

/**
 * TODO: This function implements "after" injection logic. We need to add
 * support for "before" injection logic as well.
 * https://coursera.atlassian.net/browse/CE-7660
 *
 * @param courseMaterialsData - The original course materials data.
 * @param injectableActivities - The list activities to inject into the course materials.
 * @returns A modified copy of `courseMaterialsData` with the injectable activities added around adjacent items.
 */
export const injectItemsIntoCourseMaterials = (
  courseMaterialsData: CourseMaterialsData | undefined = undefined,
  injectableActivities: InjectableActivity[] = []
) => {
  if (!courseMaterialsData) {
    return {};
  }
  if (injectableActivities.length === 0 || injectableActivities[0]?.type.typeName === 'practice-moment-control') {
    return courseMaterialsData;
  }

  // Memoization cache for module and lesson lookups
  const memoCache: Record<string, { module: CourseMaterialsDataModule; lesson: CourseMaterialsDataModuleLesson }> = {};

  const courseMaterialsDataCopy = cloneDeep(courseMaterialsData);

  // Function to find the module and lesson based on moduleId and lessonId
  function findModuleAndLesson(moduleId: string, lessonId: string) {
    const cacheKey = `${moduleId}-${lessonId}`;

    // Check if the result is already in the cache
    if (memoCache[cacheKey]) {
      return memoCache[cacheKey];
    }

    let module: CourseMaterialsDataModule | undefined;
    let lesson: CourseMaterialsDataModuleLesson | undefined;
    for (const mod of courseMaterialsDataCopy.elements) {
      if (mod.id === moduleId) {
        module = mod;
        for (const les of mod.elements) {
          if (les.id === lessonId) {
            lesson = les;
            break;
          }
        }
        break;
      }
    }

    // Cache the result before returning
    if (module && lesson) {
      memoCache[cacheKey] = { module, lesson };
    }
    return { module, lesson };
  }

  injectableActivities.forEach((injectableActivity) => {
    const item = activityToRawItem(injectableActivity);
    const {
      placement: { adjacentItemId, lessonId, moduleId },
    } = injectableActivity;
    const { lesson } = findModuleAndLesson(moduleId, lessonId);

    if (lesson) {
      // Find the index of the item after which the new item should be injected
      const itemIndex = lesson.elements.findIndex((el) => el.id === adjacentItemId);
      if (itemIndex !== -1) {
        // Insert the new item right after the found index
        lesson.elements.splice(itemIndex + 1, 0, item);
      }
    }
  });

  return courseMaterialsDataCopy;
};

/**
 * Returns a record of module IDs that have injectable activities.
 * @param injectableActivities - The list of injectable activities.
 * @returns A record of module IDs that have injectable activities.
 */
export const getModuleHasInjectableActivities = (injectableActivities: InjectableActivity[]) => {
  const moduleHasInjectableActivities: Record<string, boolean> = {};
  injectableActivities.forEach(({ placement: { moduleId } }) => {
    moduleHasInjectableActivities[moduleId] = true;
  });
  return moduleHasInjectableActivities;
};
